import {
	dateFormats,
	type DateObject,
	getProgressFromDates,
	getProgressStatus,
	ProgressStatus
} from '@capsicum/common';
import { type FC, type SVGProps } from 'react';
import styled, { useTheme } from 'styled-components';

import { Avatar, ListCard, SkeletonItem, Text } from '../../atoms';
import { CircularChart } from '../circular-chart/CircularChart';

export type ParticipationCardProps = {
	cancelling?: boolean;
	caption?: string;
	captionColor?: string;
	chartLabel?: string;
	clickable?: boolean;
	color?: string;
	endDate?: DateObject;
	icon?: FC<SVGProps<SVGSVGElement>>;
	isLoading?: boolean;
	onClick?: () => void;
	participationDate?: DateObject;
	showCaptionPrefix?: boolean;
	startDate?: DateObject;
	staticParticipationDate?: boolean;
	testID?: string;
	text?: string;
	title?: string;
};

export const ParticipationCardSkeleton = () => (
	<S.ListCardWrapper>
		<S.StatusIndicator $status={ProgressStatus.EMPTY}></S.StatusIndicator>
		<S.ListCardStart>
			<Avatar isLoading />
		</S.ListCardStart>
		<S.ListCardContent>
			<S.ListCardTitle>
				<SkeletonItem height={20} width={120} />
			</S.ListCardTitle>
			<S.ListCardText>
				<SkeletonItem height={20} width={120} />
			</S.ListCardText>
			<S.Caption color="main.disabled" variant="caption2">
				<SkeletonItem height={20} width={120} />
			</S.Caption>
		</S.ListCardContent>
		<ListCard.End></ListCard.End>
	</S.ListCardWrapper>
);

/**
 *  This component renders the in use feature with a circular progress chart
 *
 * @param {ParticipationCardProps} props - The component's props
 * @param {Date} [props.startDate] - Start date of the campaign
 * @param {Date} [props.endDate] - Due date of the campaign
 * @param {Date} [props.participationDate] - User participation date
 * @param {string} [props.text] - Center content text
 * @param {string} [props.title] - Top title
 * @param {string} [props.caption] - Bottom caption
 * @param {string} [props.chartLabel] - Label rendered under the chart
 * @param {string} [props.testID] - testID
 * @param {FC<SVGProps<SVGSVGElement>>} [props.icon] - Add left icon
 */
export const ParticipationCard = ({
	cancelling = false,
	caption,
	captionColor,
	chartLabel,
	clickable = false,
	color,
	endDate,
	icon: Icon,
	onClick,
	participationDate,
	showCaptionPrefix = true,
	startDate,
	staticParticipationDate = false,
	testID,
	text,
	title
}: ParticipationCardProps) => {
	const { colors } = useTheme();

	const { formatDateOrTimeDependingOnAge } = dateFormats;

	const progressFromDates = getProgressFromDates(startDate, endDate);
	const progressStatus = getProgressStatus(progressFromDates);

	return (
		<S.ListCardWrapper testID={testID} onClick={clickable ? onClick : undefined}>
			<S.StatusIndicator $color={color} $status={progressStatus}></S.StatusIndicator>
			<S.ListCardStart>{Icon && <Icon height={48} width={48} />}</S.ListCardStart>
			<S.ListCardContent>
				<S.ListCardTitle>{title}</S.ListCardTitle>
				<S.ListCardText>{text}</S.ListCardText>
				<S.DateWrapper>
					{participationDate && (
						<S.DateText variant="caption2">
							{staticParticipationDate
								? dateFormats.format(participationDate, 'DD/MM/YYYY')
								: formatDateOrTimeDependingOnAge(participationDate)}
						</S.DateText>
					)}
					{caption && (
						<>
							{showCaptionPrefix && <S.DateText variant="caption2">-</S.DateText>}
							<S.Caption $cancelling={cancelling} $color={captionColor} variant="caption2">
								{caption}
							</S.Caption>
						</>
					)}
				</S.DateWrapper>
			</S.ListCardContent>
			<ListCard.End>
				{endDate && progressFromDates && (
					<CircularChart
						disabled={cancelling}
						label={chartLabel}
						labelColor={colors.text.main[cancelling ? 'disabled' : 'enabled']}
						progress={progressFromDates}
						progressCircleColor={color}
					>
						<S.CircularProgressText>
							<Text color={cancelling ? 'main.disabled' : 'main.enabled'} variant="text4">
								{endDate.getDate()}
							</Text>
							<Text color={cancelling ? 'main.disabled' : 'main.enabled'} variant="text5">
								{endDate.format('MMM').toUpperCase()}
							</Text>
						</S.CircularProgressText>
					</CircularChart>
				)}
			</ListCard.End>
		</S.ListCardWrapper>
	);
};

namespace S {
	export const ListCardWrapper = styled(ListCard)`
		width: 100%;
		height: auto;
	`;
	export const ListCardStart = styled(ListCard.Start)`
		margin-left: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const ListCardContent = styled(ListCard.Content)`
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const ListCardTitle = styled(ListCard.Title)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3}
	`;
	export const ListCardText = styled(ListCard.Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title6}
	`;
	export const StatusIndicator = styled.div<{ $color?: string; $status: ProgressStatus }>`
		width: ${({ theme }) => theme.spacings.xxxsmall};
		height: 75%;
		background-color: ${({ $color, $status, theme }) =>
			$color ? $color : theme.colors.backgrounds.status[$status]};
		${({ theme }) => theme.border.radius.medium};
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		position: absolute;
	`;
	export const CircularProgressText = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;

		& span {
			line-height: 1;
		}
	`;
	export const Caption = styled(Text)<{ $cancelling?: boolean; $color?: string }>`
		color: ${({ $cancelling, $color, theme }) =>
			$color ? $color : theme.colors.text.main[$cancelling ? 'disabled' : 'enabled']};
		padding: ${({ theme }) => `${theme.spacings.xxxsmall} 0`};
	`;
	export const DateWrapper = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		justify-content: center;
		align-items: center;
	`;
	export const DateText = styled(Text)``;
}
